import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import ContestViewModal from "./ContestViewModal";
import { API_BASE_URL } from "../../utils/constants";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../components/AuthProvider";
import ContestAssignModal from "./ContestAssignModal";
import ContestBulkVideoModal from "./ContestBulkVideoModal";
import { authUrl } from "../../utils/auth";
import LotFilter, { filterByLot } from "../../components/LotFilter";

// ── Constants ─────────────────────────────────────────────────────────────────

const MEETING_LABEL = { 1: "1st Hearing", 2: "2nd Hearing", 3: "3rd Hearing" };

const STATUS_META = {
  pending:           { label: "Pending",           color: "#6c757d" },
  assigned:          { label: "Hearing Assigned",  color: "#0d6efd" },
  notice_generated:  { label: "Notice Generated",  color: "#fd7e14" },
  video_uploaded:    { label: "Video Uploaded",    color: "#6f42c1" },
  roznama_generated: { label: "Roznama Generated", color: "#0dcaf0" },
  award_uploaded:    { label: "Award Uploaded",    color: "#17a2b8" },
  done:              { label: "Done",              color: "#198754" },
};

const TABS = [
  { key: "pending",           label: "Pending Hearing" },
  { key: "assigned",          label: "Hearing Assigned" },
  { key: "notice_generated",  label: "Notice Issued" },
  { key: "video_uploaded",    label: "Pending Roznama" },
  { key: "roznama_generated", label: "Pending Awards" },
  { key: "award_uploaded",    label: "Certified Order" },
];

// ── Component ─────────────────────────────────────────────────────────────────

const ContestedCases = () => {
  const { user } = useAuth();
  const [arbId, setArbId] = useState("");
  const [cases, setCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewCase, setPreviewCase] = useState(null);
  const [showUploadNoticeModal, setShowUploadNoticeModal] = useState(false);
  const [noticePdfFile, setNoticePdfFile] = useState(null);
  const [showCertifiedOrderModal, setShowCertifiedOrderModal] = useState(false);
  const [certifiedOrderFile, setCertifiedOrderFile] = useState(null);
  const [showCertifiedAwardModal, setShowCertifiedAwardModal] = useState(false);
  const [certifiedPdfFile, setCertifiedPdfFile] = useState(null);
  const [certifiedDate, setCertifiedDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Lot filter (lot no + bank + product); "" = all lots
  const [lotFilter, setLotFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("pending");
  const [viewCase, setViewCase] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const [showBulkVideoModal, setShowBulkVideoModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const rowsPerPage = 10;

  useEffect(() => {
    if (user && user[0]) setArbId(user[0].Ref_id);
  }, [user]);

  useEffect(() => {
    if (arbId) fetchCases();
  }, [arbId]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ContestedCases?Arb_id=${arbId}`);
      if (!res.ok) throw new Error("Network error");
      const result = await res.json();
      setCases(result.data || []);
      setSelectedCases([]);
    } catch {
      toast.error("Failed to fetch contested cases");
    } finally {
      setLoading(false);
    }
  }, [arbId]);

  // ── Selection ─────────────────────────────────────────────────────────────

  const lotCases = filterByLot(cases, lotFilter);
  const tabCases = lotCases.filter(c => c.contestStatus === activeTab);

  const filteredCases = tabCases.filter(item =>
    ["referenceNo", "custName", "accNo", "bankName"].some(key =>
      item[key]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = filteredCases.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCases.length / rowsPerPage);

  const handleCheckboxChange = (item) => {
    setSelectedCases(prev =>
      prev.find(c => c.caseId === item.caseId)
        ? prev.filter(c => c.caseId !== item.caseId)
        : [...prev, item]
    );
  };

  const handleSelectAll = () => {
    setSelectedCases(
      selectedCases.length === filteredCases.length ? [] : [...filteredCases]
    );
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedCases([]);
  };

  // ── Tab counts ────────────────────────────────────────────────────────────

  const countByStatus = (status) =>
    lotCases.filter(c => c.contestStatus === status).length;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleOpenUploadNoticeModal = () => {
    if (selectedCases.length !== 1) {
      toast.info("Please select only one case to upload hearing notice");
      return;
    }
    setPreviewCase(selectedCases[0]);
    setNoticePdfFile(null);
    setShowUploadNoticeModal(true);
  };

  const handleSubmitNoticeFile = async () => {
    if (!previewCase || !noticePdfFile) {
      toast.error("Please select a PDF file", { theme: "colored", autoClose: 2000 });
      return;
    }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", noticePdfFile);
      const res = await fetch(
        `${API_BASE_URL}/api/contest/notice/upload?Case_id=${previewCase.caseId}`,
        { method: "POST", body: formData }
      );
      if (res.ok) {
        toast.success("Hearing notice uploaded successfully", { theme: "colored", autoClose: 2000 });
        setShowUploadNoticeModal(false);
        setNoticePdfFile(null);
        setPreviewCase(null);
        await fetchCases();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.message || "Failed to upload hearing notice", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoznama = async (caseId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contest/roznama?Case_id=${caseId}`, { method: "POST" });
      if (res.ok) {
        toast.success("Roznama generated successfully", { theme: "colored", autoClose: 2000 });
        await fetchCases();
      } else {
        toast.error("Failed to generate roznama", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadNotice = (caseId) => {
    window.open(authUrl(`${API_BASE_URL}/api/contest/notice/file?Case_id=${caseId}`), "_blank");
  };

  const handleDownloadRoznama = (caseId) => {
    window.open(authUrl(`${API_BASE_URL}/api/contest/roznama/file?Case_id=${caseId}`), "_blank");
  };

  const handleDownloadAward = (caseId) => {
    window.open(authUrl(`${API_BASE_URL}/api/contest/order/file?Case_id=${caseId}`), "_blank");
  };

  const handleDownloadCertifiedAward = (caseId) => {
    window.open(authUrl(`${API_BASE_URL}/api/contest/award/file?Case_id=${caseId}`), "_blank");
  };

  const handleResetToPending = async () => {
    setShowResetConfirm(false);
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contest/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseIds: selectedCases.map(c => c.caseId) }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success(data.message || "Cases moved to Pending Hearing", { theme: "colored", autoClose: 2000 });
        await fetchCases();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to reset cases", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCertifiedOrderModal = () => {
    if (selectedCases.length !== 1) {
      toast.info("Please select exactly one case to upload certified order");
      return;
    }
    setPreviewCase(selectedCases[0]);
    setCertifiedOrderFile(null);
    setShowCertifiedOrderModal(true);
  };

  const handleSubmitCertifiedOrder = async () => {
    if (!previewCase || !certifiedOrderFile) {
      toast.error("Please select a PDF file", { theme: "colored", autoClose: 2000 });
      return;
    }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", certifiedOrderFile);
      const res = await fetch(
        `${API_BASE_URL}/api/contest/order/upload?Case_id=${previewCase.caseId}`,
        { method: "POST", body: formData }
      );
      if (res.ok) {
        toast.success("Certified order uploaded successfully", { theme: "colored", autoClose: 2000 });
        setShowCertifiedOrderModal(false);
        setCertifiedOrderFile(null);
        setPreviewCase(null);
        await fetchCases();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.message || "Failed to upload certified order", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };



  const handleGenerateRoznamaForCase = async (caseItem) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contest/roznama?Case_id=${caseItem.caseId}`, { method: "POST" });
      if (res.ok) {
        toast.success("Roznama generated", { theme: "colored", autoClose: 2000 });
        await fetchCases();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to generate roznama", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateRoznama = async () => {
    setActionLoading(true);
    try {
      const pending = selectedCases.filter(c => !c.hasRoznama);
      let successCount = 0;
      let failCount = 0;
      for (const c of pending) {
        const res = await fetch(`${API_BASE_URL}/api/contest/roznama?Case_id=${c.caseId}`, { method: "POST" });
        if (res.ok) successCount++;
        else failCount++;
      }
      if (successCount) toast.success(`Roznama generated for ${successCount} case(s)`, { theme: "colored", autoClose: 2000 });
      if (failCount)    toast.error(`Failed for ${failCount} case(s)`, { theme: "colored", autoClose: 2000 });
      await fetchCases();
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkVideoUpload = async ({ videoFile }) => {
    if (!videoFile) {
      toast.error("Please select a video file", { theme: "colored", autoClose: 2000 });
      return;
    }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("video", videoFile);

      const caseIds = selectedCases.map(c => c.caseId).join(",");
      const params = new URLSearchParams({ Case_ids: caseIds });

      const res = await fetch(`${API_BASE_URL}/api/contest/video-bulk?${params.toString()}`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success(data.message || "Video uploaded successfully", { theme: "colored", autoClose: 2000 });
        setShowBulkVideoModal(false);
        await fetchCases();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to upload video", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCertifiedAwardModal = () => {
    if (selectedCases.length !== 1) {
      toast.info("Please select exactly one case to upload certified award");
      return;
    }
    setPreviewCase(selectedCases[0]);
    setCertifiedPdfFile(null);
    setCertifiedDate("");
    setShowCertifiedAwardModal(true);
  };

  const handleUploadCertifiedAward = async () => {
    if (!previewCase || !certifiedPdfFile) {
      toast.error("Please select a PDF file", { theme: "colored", autoClose: 2000 });
      return;
    }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", certifiedPdfFile);

      const params = new URLSearchParams();
      params.append("Case_id", previewCase.caseId.toString());
      if (certifiedDate) params.append("Certified_date", certifiedDate);

      const res = await fetch(`${API_BASE_URL}/api/contest/award/certified?${params.toString()}`, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        toast.success("Certified award uploaded successfully", { theme: "colored", autoClose: 2000 });
        setShowCertifiedAwardModal(false);
        setCertifiedPdfFile(null);
        setCertifiedDate("");
        setPreviewCase(null);
        await fetchCases();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.message || "Failed to upload certified award", { theme: "colored", autoClose: 2000 });
      }
    } catch {
      toast.error("An error occurred", { theme: "colored", autoClose: 2000 });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Action bar config ──────────────────────────────────────────────────────
  // Returns the relevant action button for the active tab

  const renderActionBar = () => {
    const count = selectedCases.length;

    if (activeTab === "pending") {
      return (
        <button
          className="custBtn"
          disabled={count === 0 || actionLoading}
          onClick={() => setShowAssignModal(true)}
          style={{ opacity: count === 0 ? 0.5 : 1 }}
        >
          Assign Next Hearing {count > 0 ? `(${count})` : ""}
        </button>
      );
    }
    if (activeTab === "assigned") {
      return (
        <button
          className="custBtn"
          disabled={count === 0 || actionLoading}
          onClick={handleOpenUploadNoticeModal}
          style={{ opacity: count === 0 ? 0.5 : 1 }}
        >
          {actionLoading ? "Uploading…" : `Upload Hearing Notice ${count > 0 ? `(${count})` : ""}`}
        </button>
      );
    }
    if (activeTab === "notice_generated") {
      // Only action here is video upload — once uploaded, case moves to Pending Roznama tab
      return (
        <button
          className="custBtn"
          disabled={count === 0 || actionLoading}
          onClick={() => setShowBulkVideoModal(true)}
          style={{ opacity: count === 0 ? 0.5 : 1, whiteSpace: "nowrap" }}
        >
          {actionLoading ? "Uploading…" : `Upload Video${count > 0 ? ` (${count})` : ""}`}
        </button>
      );
    }
    if (activeTab === "video_uploaded") {
      // Generate roznama for selected cases that don't have it yet
      const eligibleCases = selectedCases.filter(c => !c.hasRoznama);
      if (count === 0 || eligibleCases.length === 0) return null;
      return (
        <button
          className="custBtn"
          disabled={actionLoading}
          onClick={handleGenerateRoznama}
          style={{ whiteSpace: "nowrap" }}
        >
          {actionLoading ? "Generating…" : `Generate Roznama (${eligibleCases.length})`}
        </button>
      );
    }
    if (activeTab === "roznama_generated") {
      // Pending Awards: Re-Hearing OR Upload Award (1st doc)
      return (
        <div className="d-inline-flex align-items-center gap-2">
          <button
            className="btn"
            disabled={count === 0 || actionLoading}
            onClick={() => setShowResetConfirm(true)}
            style={{
              background: "transparent",
              border: "1px solid #f0a500",
              color: "#f0a500",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              opacity: count === 0 ? 0.5 : 1,
              cursor: count === 0 ? "not-allowed" : "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { if (count > 0) { e.currentTarget.style.background = "#f0a500"; e.currentTarget.style.color = "#fff"; }}}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#f0a500"; }}
          >
            ↩ Re-Hearing{count > 0 ? ` (${count})` : ""}
          </button>
          <button
            className="custBtn"
            disabled={count === 0 || actionLoading}
            onClick={handleOpenCertifiedOrderModal}
            style={{ opacity: count === 0 ? 0.5 : 1, whiteSpace: "nowrap" }}
          >
            {actionLoading ? "Uploading…" : `Upload Award${count > 0 ? ` (${count})` : ""}`}
          </button>
        </div>
      );
    }
    if (activeTab === "award_uploaded") {
      // Certified Order tab: Upload Certified Award (2nd doc) → Done
      return (
        <button
          className="custBtn"
          disabled={count === 0 || actionLoading}
          onClick={handleOpenCertifiedAwardModal}
          style={{ opacity: count === 0 ? 0.5 : 1, whiteSpace: "nowrap" }}
        >
          {actionLoading ? "Uploading…" : `Upload Certified Award${count > 0 ? ` (${count})` : ""}`}
        </button>
      );
    }
    return null;
  };

  if (loading) return <LoadingSpinner />;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid px-3">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="mb-3">
        <h5 className="mb-1">Contested Cases</h5>
        <small className="text-muted">Independent workflow for cases contested during hearings</small>
      </div>

      {/* ── Workflow Status Tabs ─────────────────────────────────────────────── */}
      <div className="d-flex gap-1 flex-wrap mb-0" style={{ borderBottom: "2px solid #dee2e6" }}>
        {TABS.map(tab => {
          const count = countByStatus(tab.key);
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                border: "none",
                borderBottom: isActive ? "3px solid #f0a500" : "3px solid transparent",
                background: "none",
                padding: "8px 14px",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#f0a500" : "#555",
                cursor: "pointer",
                fontSize: "0.85rem",
                transition: "all 0.15s",
              }}
            >
              {tab.label}
              <span
                style={{
                  marginLeft: "6px",
                  background: isActive ? "#f0a500" : "#dee2e6",
                  color: isActive ? "#fff" : "#555",
                  borderRadius: "10px",
                  padding: "1px 7px",
                  fontSize: "0.75rem",
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {cases.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No contested cases found.</p>
        </div>
      ) : (
        <>
          {/* ── Search + Action Bar ───────────────────────────────────────────── */}
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2 mt-2">
            <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Search by Reference No, Customer Name, Account No or Bank…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="form-control"
                style={{ maxWidth: "380px", fontSize: "0.8rem" }}
              />
              <LotFilter
                rows={cases}
                value={lotFilter}
                onChange={next => { setLotFilter(next); setCurrentPage(1); setSelectedCases([]); }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              {selectedCases.length === 0 && activeTab !== "video_uploaded" && (
                <small className="text-muted">Select cases to take action</small>
              )}
              {renderActionBar()}
            </div>
          </div>

          {/* ── Table ───────────────────────────────────────────────────────── */}
          <div className="table-responsive">
            <table
              className="table table-striped table-bordered table-hover table-sm mt-1 text-center"
              style={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "45px" }}>
                    <input
                      type="checkbox"
                      checked={selectedCases.length === filteredCases.length && filteredCases.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: "50px" }}>Sr No</th>
                  <th>Reference No</th>
                  <th>Customer Name</th>
                  <th style={{ width: "110px" }}>Account No</th>
                  <th style={{ width: "70px" }}>Lot No</th>
                  <th>Bank</th>
                  <th style={{ width: "120px" }}>Contested At</th>
                  <th style={{ width: "110px" }}>Contested Date</th>
                  <th style={{ width: "110px" }}>Next Hearing</th>
                  <th style={{ width: "145px" }}>Status</th>
                  <th style={{ width: "210px", position: "sticky", right: 0, background: "#fff", zIndex: 1 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentRows.length > 0 ? (
                  currentRows.map((item, index) => {
                    const meta = STATUS_META[item.contestStatus] || STATUS_META.pending;
                    const isSelected = selectedCases.find(c => c.caseId === item.caseId);
                    return (
                      <tr
                        key={item.caseId}
                        onClick={() => handleCheckboxChange(item)}
                        style={{ cursor: "pointer", verticalAlign: "middle" }}
                      >
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => handleCheckboxChange(item)}
                          />
                        </td>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>{item.referenceNo}</td>
                        <td style={{ textAlign: "left" }}>{item.custName}</td>
                        <td>{item.accNo}</td>
                        <td>{item.lotNo}</td>
                        <td style={{ textAlign: "left" }}>{item.bankName}</td>
                        <td>{MEETING_LABEL[item.contestedMeetingNo] ?? "—"}</td>
                        <td>{item.contestedDate || "—"}</td>
                        <td>{item.contestNextHearingDate || "—"}</td>
                        <td>
                          <span
                            style={{
                              background: meta.color,
                              color: "#fff",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "0.75rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td style={{ position: "sticky", right: 0, background: "#fff", width: "210px" }} onClick={e => e.stopPropagation()}>
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            {activeTab === "video_uploaded" && (() => {
                              if (!item.hasRoznama) {
                                return (
                                  <button
                                    className="custBtn"
                                    style={{ fontSize: "0.75rem", padding: "3px 10px", whiteSpace: "nowrap" }}
                                    disabled={actionLoading}
                                    onClick={() => handleGenerateRoznamaForCase(item)}
                                  >
                                    Generate Roznama
                                  </button>
                                );
                              }
                              return (
                                <span className="text-success fw-semibold" style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                                  ✓ Roznama Generated
                                </span>
                              );
                            })()}
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              title="View Details"
                              onClick={() => { setViewCase(item); setShowViewModal(true); }}
                            >
                              <FaEye />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center text-muted py-3">
                      {searchTerm ? "No results found" : `No ${(TABS.find(t => t.key === activeTab)?.label ?? activeTab)} cases`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ─────────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <nav>
                <ul className="pagination pagination-sm">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {/* ── Bulk Video Upload Modal ──────────────────────────────────────────── */}
      <ContestBulkVideoModal
        show={showBulkVideoModal}
        onHide={() => setShowBulkVideoModal(false)}
        selectedCases={selectedCases}
        onUpload={handleBulkVideoUpload}
        loading={actionLoading}
      />

      {/* ── View Details Modal ──────────────────────────────────────────────── */}
      <ContestViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        viewCase={viewCase}
        onDownloadNotice={handleDownloadNotice}
        onDownloadRoznama={handleDownloadRoznama}
        onDownloadAward={handleDownloadAward}
        onDownloadCertifiedAward={handleDownloadCertifiedAward}
      />

      {/* ── Assign Next Hearing Modal ────────────────────────────────────────── */}
      <ContestAssignModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        selectedCases={selectedCases}
        arbId={arbId}
        onSuccess={async () => {
          toast.success(`${selectedCases.length} case(s) assigned successfully`, { theme: "colored", autoClose: 2000 });
          await fetchCases();
        }}
      />

      {/* ── Upload Hearing Notice Modal ──────────────────────────────────────────── */}
      <Modal show={showUploadNoticeModal} onHide={() => setShowUploadNoticeModal(false)} centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Upload Hearing Notice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewCase && (
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Case: <strong>{previewCase.referenceNo}</strong> — {previewCase.custName}
            </p>
          )}
          <div className="mb-3">
            <label className="form-label">Hearing Notice PDF</label>
            <input
              type="file"
              accept=".pdf"
              className="form-control"
              onChange={(e) => setNoticePdfFile(e.target.files?.[0] || null)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowUploadNoticeModal(false)} disabled={actionLoading}>
            Cancel
          </button>
          <button className="custBtn" onClick={handleSubmitNoticeFile} disabled={actionLoading || !noticePdfFile}>
            {actionLoading ? "Uploading..." : "Upload Hearing Notice"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Reset to Pending Hearing Confirmation ────────────────────────────── */}
      <Modal show={showResetConfirm} onHide={() => setShowResetConfirm(false)} centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Move to Pending Hearing?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.875rem" }}>
            This will reset <strong>{selectedCases.length}</strong> case(s) back to <strong>Pending Hearing</strong> and clear all hearing progress (video, details, notices).
          </p>
          <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
            Use this when the case needs another hearing before an award can be passed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowResetConfirm(false)}>
            Cancel
          </button>
          <button className="custBtn" onClick={handleResetToPending} disabled={actionLoading}>
            {actionLoading ? "Resetting…" : "Yes, Move to Pending Hearing"}
          </button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />

      {/* ── Upload Award Modal (1st doc) ─────────────────────────────────────────── */}
      <Modal show={showCertifiedOrderModal} onHide={() => setShowCertifiedOrderModal(false)} centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Upload Award</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewCase && (
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Case: <strong>{previewCase.referenceNo}</strong> — {previewCase.custName}
            </p>
          )}
          <div className="mb-3">
            <label className="form-label">Award PDF</label>
            <input
              type="file"
              accept=".pdf"
              className="form-control"
              onChange={(e) => setCertifiedOrderFile(e.target.files?.[0] || null)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowCertifiedOrderModal(false)} disabled={actionLoading}>
            Cancel
          </button>
          <button className="custBtn" onClick={handleSubmitCertifiedOrder} disabled={actionLoading || !certifiedOrderFile}>
            {actionLoading ? "Uploading..." : "Upload Award"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Upload Certified Award Modal ─────────────────────────────────────────── */}
      <Modal show={showCertifiedAwardModal} onHide={() => setShowCertifiedAwardModal(false)} centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Upload Certified Award</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewCase && (
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Case: <strong>{previewCase.referenceNo}</strong> — {previewCase.custName}
            </p>
          )}
          <div className="mb-3">
            <label className="form-label">Certified Award PDF</label>
            <input
              type="file"
              accept=".pdf"
              className="form-control"
              onChange={(e) => setCertifiedPdfFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Certified Date (optional)</label>
            <input
              type="date"
              className="form-control"
              value={certifiedDate}
              onChange={(e) => setCertifiedDate(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowCertifiedAwardModal(false)} disabled={actionLoading}>
            Cancel
          </button>
          <button className="custBtn" onClick={handleUploadCertifiedAward} disabled={actionLoading || !certifiedPdfFile}>
            {actionLoading ? "Uploading..." : "Upload Certified Award"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContestedCases;
